import type { TicketAttachmentDTO } from "@zplanem/responses"
import type { Translations } from "@zplanem/translations"
import { useRef, useState } from "react"
import { Button } from "@/components/ui/button"
import { ApiError } from "../../../lib/api-client"
import {
	useDeleteTicketAttachment,
	useUploadTicketAttachment,
} from "../../hooks/use-tickets"
import { canDeleteTicketAttachment } from "./ticket-attachments-model"

type TicketCopy = Translations["tickets"]

const ACCEPTED_TICKET_ATTACHMENT_TYPES = "image/*,application/pdf,video/*"

export function TicketAttachments({
	propertyId,
	ticketId,
	attachments,
	t,
	target,
	canDelete,
	viewerId,
	showEmpty = false,
	compact = false,
}: {
	propertyId: string
	ticketId: string
	attachments: TicketAttachmentDTO[]
	t: TicketCopy
	target?: { commentId: string }
	canDelete: boolean
	viewerId?: string
	showEmpty?: boolean
	compact?: boolean
}) {
	const inputRef = useRef<HTMLInputElement>(null)
	const upload = useUploadTicketAttachment(propertyId, ticketId)
	const remove = useDeleteTicketAttachment(propertyId, ticketId)
	const [error, setError] = useState<string | null>(null)

	async function uploadFile(file: File) {
		setError(null)
		try {
			await upload.mutateAsync({ file, target })
		} catch (err) {
			setError(err instanceof ApiError ? err.message : t.attachmentUploadError)
		}
	}

	async function deleteAttachment(fileId: string) {
		setError(null)
		try {
			await remove.mutateAsync(fileId)
		} catch (err) {
			setError(err instanceof ApiError ? err.message : t.attachmentDeleteError)
		}
	}

	return (
		<div className={compact ? "space-y-2" : "space-y-3"}>
			<input
				ref={inputRef}
				type="file"
				accept={ACCEPTED_TICKET_ATTACHMENT_TYPES}
				className="hidden"
				onChange={(e) => {
					const file = e.currentTarget.files?.[0]
					e.currentTarget.value = ""
					if (file) void uploadFile(file)
				}}
			/>

			{attachments.length > 0 ? (
				<div
					className={
						compact ? "flex flex-wrap gap-2" : "grid gap-2 sm:grid-cols-2"
					}
				>
					{attachments.map((attachment) => (
						<AttachmentItem
							key={attachment.id}
							attachment={attachment}
							t={t}
							canDelete={canDeleteTicketAttachment(
								attachment,
								viewerId,
								canDelete,
							)}
							pending={remove.isPending}
							onDelete={() => {
								void deleteAttachment(attachment.id)
							}}
						/>
					))}
				</div>
			) : showEmpty ? (
				<p className="text-sm text-muted-foreground">{t.noAttachments}</p>
			) : null}

			<div>
				<Button
					type="button"
					variant="outline"
					size="sm"
					disabled={upload.isPending}
					onClick={() => inputRef.current?.click()}
				>
					{upload.isPending ? t.attachmentUploading : t.attachmentUpload}
				</Button>
			</div>

			{error ? (
				<div role="alert" className="text-sm text-destructive">
					{error}
				</div>
			) : null}
		</div>
	)
}

function AttachmentItem({
	attachment,
	t,
	canDelete,
	pending,
	onDelete,
}: {
	attachment: TicketAttachmentDTO
	t: TicketCopy
	canDelete: boolean
	pending: boolean
	onDelete: () => void
}) {
	const label =
		attachment.originalFilename ?? attachment.mimeType ?? attachment.kind
	return (
		<div className="flex min-w-0 items-center gap-2 rounded-lg border border-border bg-muted/30 px-2 py-2">
			<a
				href={attachment.url}
				target="_blank"
				rel="noopener noreferrer"
				aria-label={t.attachmentOpen}
				className="flex min-w-0 flex-1 items-center gap-2 rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
			>
				{attachment.kind === "image" && attachment.thumbnailUrl ? (
					<img
						src={attachment.thumbnailUrl}
						alt=""
						loading="lazy"
						className="h-10 w-10 shrink-0 rounded-md object-cover"
					/>
				) : (
					<span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-background text-[10px] font-semibold uppercase text-muted-foreground">
						{attachment.kind}
					</span>
				)}
				<span className="min-w-0 truncate text-sm text-foreground">
					{label}
				</span>
			</a>
			{canDelete ? (
				<Button
					type="button"
					variant="ghost"
					size="sm"
					disabled={pending}
					onClick={onDelete}
				>
					{t.attachmentDelete}
				</Button>
			) : null}
		</div>
	)
}
